import React from 'react';
import { AgnesModal, AgnesButton, AgnesInput, AgnesTextarea, AgnesCheckbox } from '@agnes/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { usePerfisStore, type Permissao } from '@/stores/usePerfisStore';
import { notify } from '@/stores/useToast';

type Perfil = any;

interface Props {
  open: boolean;
  onClose: () => void;
  perfil?: Perfil | null; // presente = edição
}

interface Feature {
  base: string;
  label: string;
  readPerm?: Permissao;
  writePerm?: Permissao;
}

function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// Agrupa por `group`, ordena grupos e, dentro, pareia read/write por código-base (feature).
// Paridade com o legado permissionsByGroup + getGroupFeatures.
function agrupar(permissoes: Permissao[]): Record<string, Feature[]> {
  const groups: Record<string, Permissao[]> = {};
  for (const p of permissoes) {
    const g = p.group || 'Outros';
    (groups[g] ||= []).push(p);
  }
  const out: Record<string, Feature[]> = {};
  for (const g of Object.keys(groups).sort()) {
    const feats: Record<string, Feature> = {};
    for (const perm of groups[g].sort((a, b) => a.code.localeCompare(b.code))) {
      const base = perm.code.replace(/:read$|:write$/, '');
      const feat = (feats[base] ||= {
        base,
        label: (perm.display_name || base).replace(/ - (Visualizar|Editar)$/i, ''),
      });
      if (perm.code.endsWith(':read')) feat.readPerm = perm;
      else if (perm.code.endsWith(':write')) feat.writePerm = perm;
      else feat.readPerm = perm; // permissão single (consulta) = trata como "acesso"
    }
    out[g] = Object.values(feats);
  }
  return out;
}

export default function PerfilFormModal({ open, onClose, perfil }: Props) {
  const editando = Boolean(perfil?.id);
  const permissoes = usePerfisStore((s) => s.permissoes);
  const [nome, setNome] = React.useState('');
  const [descricao, setDescricao] = React.useState('');
  const [isSystem, setIsSystem] = React.useState(false);
  // CON-5193: Super Administrador é sistêmico E imutável — acesso total, sem edição
  // de permissões (diferente dos demais perfis de sistema, que permanecem editáveis).
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [abertos, setAbertos] = React.useState<Set<string>>(new Set());
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    usePerfisStore.getState().carregarPermissoes();
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelado = false;
    async function carregar() {
      if (editando) {
        const full = await usePerfisStore.getState().buscarPerfil(perfil.id);
        if (cancelado || !full) return;
        setNome(full.display_name ?? full.name ?? '');
        setDescricao(full.description ?? '');
        setIsSystem(Boolean(full.is_system));
        setIsSuperAdmin(Boolean(full.is_super_admin));
        setSelected(new Set((full.permissions ?? []).map((p: Permissao) => p.id)));
      } else {
        setNome('');
        setDescricao('');
        setIsSystem(false);
        setIsSuperAdmin(false);
        setSelected(new Set());
      }
    }
    carregar();
    return () => {
      cancelado = true;
    };
  }, [open, editando, perfil]);

  const grupos = React.useMemo(() => agrupar(permissoes), [permissoes]);

  function toggle(id?: string) {
    if (!id || isSuperAdmin) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Header do grupo por ação (read|write): estado all/partial + toggle em massa.
  function permsDaAcao(feats: Feature[], acao: 'read' | 'write'): Permissao[] {
    return feats
      .map((f) => (acao === 'read' ? f.readPerm : f.writePerm))
      .filter(Boolean) as Permissao[];
  }
  function estadoGrupo(feats: Feature[], acao: 'read' | 'write'): boolean | 'indeterminate' {
    const perms = permsDaAcao(feats, acao);
    if (!perms.length) return false;
    const marcados = perms.filter((p) => selected.has(p.id)).length;
    if (marcados === 0) return false;
    return marcados === perms.length ? true : 'indeterminate';
  }
  function toggleGrupo(feats: Feature[], acao: 'read' | 'write', marcar: boolean) {
    if (isSuperAdmin) return;
    const perms = permsDaAcao(feats, acao);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of perms) {
        if (marcar) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  }

  function toggleAberto(g: string) {
    setAbertos((prev) => {
      const n = new Set(prev);
      if (n.has(g)) n.delete(g);
      else n.add(g);
      return n;
    });
  }

  const podeSalvar = nome.trim().length > 0 && !salvando;

  async function salvar() {
    // Super Administrador: acesso total e imutável — não há nada pra salvar (CON-5193).
    if (isSuperAdmin) {
      onClose();
      return;
    }
    setSalvando(true);
    try {
      const ids = [...selected];
      const store = usePerfisStore.getState();
      if (editando) {
        // Perfis de sistema não têm metadados editáveis, mas permissões sim (paridade legado).
        if (!isSystem) {
          await store.atualizar(perfil.id, {
            display_name: nome.trim(),
            description: descricao.trim() || null,
          });
        }
        await store.atribuirPermissoes(perfil.id, ids);
        notify.success('Perfil atualizado.');
      } else {
        await store.criar({
          name: slugify(nome),
          display_name: nome.trim(),
          description: descricao.trim() || null,
          permission_ids: ids,
        });
        notify.success('Perfil criado.');
      }
      await store.buscar();
      onClose();
    } catch {
      notify.error('Erro ao salvar perfil.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <AgnesModal
      open={open}
      title={editando ? 'Editar perfil' : 'Novo perfil'}
      maxWidth="lg"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton variant="primary" disabled={!podeSalvar} onClick={salvar}>
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <AgnesInput
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome do perfil *"
          disabled={isSystem}
        />
        <AgnesTextarea
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Descrição"
          rows={2}
          disabled={isSystem}
        />
        {isSuperAdmin ? (
          <p className="text-xs text-agnes-text-muted">
            Perfil de sistema: acesso total à plataforma. Nome, descrição e permissões são fixos e
            não podem ser alterados.
          </p>
        ) : (
          isSystem && (
            <p className="text-xs text-agnes-text-muted">
              Perfil de sistema: nome e descrição são fixos; as permissões podem ser ajustadas.
            </p>
          )
        )}

        <div className="flex flex-col gap-2 max-h-[46vh] overflow-y-auto pr-1">
          {Object.keys(grupos).length === 0 && (
            <p className="text-sm text-agnes-text-muted">Carregando permissões…</p>
          )}
          {Object.entries(grupos).map(([g, feats]) => {
            const aberto = abertos.has(g);
            const temWrite = permsDaAcao(feats, 'write').length > 0;
            return (
              <div key={g} className="rounded-xl border border-agnes-border bg-agnes-bg-surface">
                <div className="grid grid-cols-[1fr_7rem_5rem] items-center gap-3 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleAberto(g)}
                    className="flex items-center gap-2 font-medium"
                  >
                    {aberto ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {g}
                  </button>
                  <AgnesCheckbox
                    label="Visualizar"
                    checked={isSuperAdmin ? true : estadoGrupo(feats, 'read')}
                    disabled={isSuperAdmin}
                    onCheckedChange={(v) => toggleGrupo(feats, 'read', v === true)}
                  />
                  {temWrite ? (
                    <AgnesCheckbox
                      label="Editar"
                      checked={isSuperAdmin ? true : estadoGrupo(feats, 'write')}
                      disabled={isSuperAdmin}
                      onCheckedChange={(v) => toggleGrupo(feats, 'write', v === true)}
                    />
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </div>
                {aberto && (
                  <div className="border-t border-agnes-border px-3 py-2">
                    {feats.map((f) => (
                      <div
                        key={f.base}
                        className="grid grid-cols-[1fr_7rem_5rem] items-center gap-3 py-1"
                      >
                        <span className="text-sm">{f.label}</span>
                        <AgnesCheckbox
                          label="Ver"
                          disabled={isSuperAdmin || !f.readPerm}
                          checked={
                            isSuperAdmin ? true : f.readPerm ? selected.has(f.readPerm.id) : false
                          }
                          onCheckedChange={() => toggle(f.readPerm?.id)}
                        />
                        {f.writePerm ? (
                          <AgnesCheckbox
                            label="Editar"
                            disabled={isSuperAdmin}
                            checked={isSuperAdmin ? true : selected.has(f.writePerm.id)}
                            onCheckedChange={() => toggle(f.writePerm?.id)}
                          />
                        ) : (
                          <span aria-hidden="true" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AgnesModal>
  );
}
